import type { ReactNode } from 'react';

import { InfoTooltip } from '@/components/InfoTooltip';
import {
  TrendIndicator,
  type TrendChangeKind,
} from '@/components/TrendIndicator';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

const SIZE = {
  /** A page's KPI row (Usage, AI usage): 24px value. */
  default: {
    label: 'text-sm',
    value: 'text-2xl',
    aside: 'text-base',
    sub: 'text-xs',
    skeleton: 'h-8 w-24',
    trend: 'default',
  },
  /** A summary inside a card (Home's totals): 20px value. */
  md: {
    label: 'text-sm',
    value: 'text-xl',
    aside: 'text-sm',
    sub: 'text-xs',
    skeleton: 'h-7 w-24',
    trend: 'sm',
  },
  /** Dense grids (AI credits, a machine's figures): 14px value. */
  sm: {
    label: 'text-xs',
    value: 'text-sm',
    aside: 'text-xs',
    sub: 'text-2xs',
    skeleton: 'h-5 w-16',
    trend: 'sm',
  },
} as const;

const FRAME = {
  /** Its own Card, in a grid of tiles on a page. */
  card: '',
  /** A tinted box inside a card. */
  muted: 'rounded-md bg-muted/50 p-3',
  /** No box: a grid that has its own. */
  none: '',
} as const;

export type StatTileSize = keyof typeof SIZE;
export type StatTileFrame = keyof typeof FRAME;

export interface StatTileProps {
  label: string;
  /** What the figure is, in a tooltip on an (i) beside the label. Not a sentence under it. */
  info?: ReactNode;
  /** The figure, already formatted with @/lib/formatters. */
  value: ReactNode;
  /** A smaller figure after the value: a share ("41.2%"), a total ("of 120"), a ratio ("4.2x"). */
  aside?: ReactNode;
  /** `danger` for an aside that is a problem ("3 need a look"). @default 'muted' */
  asideTone?: 'muted' | 'danger';
  /** A line under the value: context such as "of $50 budget" or "in 12 runs". */
  sub?: ReactNode;
  /**
   * The change against the earlier period, signed: a fraction for `percent`, points for `points`
   * (`changeFrom` / `pointsFrom` from `@/lib/period-change`). Null hides it.
   */
  change?: number | null;
  /** @default 'percent' */
  changeKind?: TrendChangeKind;
  /** Whether a rise is good news (false for costs). @default true */
  upIsGood?: boolean;
  /** What the change is against, for screen readers: "year over year". */
  changeLabel?: string;
  /** Shows a placeholder in place of the value. */
  loading?: boolean;
  /** @default 'default' */
  size?: StatTileSize;
  /** @default 'card' */
  frame?: StatTileFrame;
  /** The tile is the content of a link or button: it shows hover. The link holds focus and the label. */
  interactive?: boolean;
  /** Placement only (grid span, width). */
  className?: string;
}

/**
 * One KPI: a muted label (with an optional info tooltip), the figure, an optional aside and sub-line,
 * and the change against the earlier period (TrendIndicator's signed mode). Figures come in formatted;
 * the change is formatted here.
 */
export function StatTile({
  label,
  info,
  value,
  aside,
  asideTone = 'muted',
  sub,
  change,
  changeKind = 'percent',
  upIsGood = true,
  changeLabel,
  loading = false,
  size = 'default',
  frame = 'card',
  interactive = false,
  className,
}: StatTileProps) {
  const s = SIZE[size];
  const body = (
    <div className="min-w-0 space-y-1">
      <div className={cn('flex min-w-0 items-center gap-1.5 text-muted-foreground', s.label)}>
        <span className="truncate">{label}</span>
        {info && <InfoTooltip label={label}>{info}</InfoTooltip>}
      </div>
      {loading ? (
        <Skeleton className={s.skeleton} />
      ) : (
        <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className={cn('min-w-0 font-semibold tracking-tight tabular-nums', s.value)}>
            {value}
          </span>
          {aside != null && aside !== false && (
            <span
              className={cn(
                'tabular-nums',
                s.aside,
                asideTone === 'danger' ? 'text-danger' : 'text-muted-foreground'
              )}
            >
              {aside}
            </span>
          )}
          <TrendIndicator
            change={change}
            kind={changeKind}
            upIsGood={upIsGood}
            label={changeLabel}
            size={s.trend}
          />
        </div>
      )}
      {sub != null && sub !== false && (
        <div className={cn('text-muted-foreground', s.sub)}>{sub}</div>
      )}
    </div>
  );

  if (frame === 'card') {
    return (
      <Card
        className={cn(
          interactive && 'transition-colors hover:bg-muted/40',
          className
        )}
      >
        <CardContent>{body}</CardContent>
      </Card>
    );
  }
  return (
    <div
      className={cn(
        'min-w-0',
        FRAME[frame],
        interactive && 'transition-colors hover:bg-muted',
        className
      )}
    >
      {body}
    </div>
  );
}
